import { AlertCircle, Inbox, RefreshCw } from "lucide-react";
import type { ReactNode } from "react";

/** Shown when a data query fails, with an optional retry. */
export function ErrorState({
  message = "No pudimos cargar los datos.",
  onRetry,
}: {
  message?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-red-100 bg-red-50/50 px-6 py-12 text-center my-4 animate-fadeIn">
      <AlertCircle className="h-8 w-8 text-red-500" />
      <p className="text-sm font-medium text-red-700">{message}</p>
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className="mt-1 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-xs font-semibold text-white hover:bg-red-700 transition-colors shadow-xs"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Reintentar
        </button>
      )}
    </div>
  );
}

/** Shown when a query succeeds but returns no rows. */
export function EmptyState({
  title = "Nada por aquí",
  message,
  icon,
  action,
}: {
  title?: string;
  message?: string;
  icon?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-zinc-200 px-6 py-14 text-center my-4 bg-white/50">
      <div className="text-zinc-300 mb-1">{icon ?? <Inbox className="h-10 w-10 stroke-1" />}</div>
      <p className="text-sm font-bold text-zinc-700">{title}</p>
      {message && <p className="max-w-sm text-xs text-zinc-500 leading-relaxed">{message}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** Simple "load more" button for paginated lists. */
export function LoadMore({
  hasMore,
  loading,
  shown,
  total,
  onClick,
}: {
  hasMore: boolean;
  loading?: boolean;
  shown: number;
  total: number;
  onClick: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-2 py-4">
      <p className="text-xs font-medium text-zinc-400">
        Mostrando {shown} de {total}
      </p>
      {hasMore && (
        <button
          type="button"
          onClick={onClick}
          disabled={loading}
          className="rounded-xl border border-zinc-200 bg-white px-4 py-2 text-xs font-semibold text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 transition-all shadow-xs"
        >
          {loading ? "Cargando…" : "Cargar más"}
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
 * UNIFIED SKELETON LOADERS FOR SYSTEMIC HOMOGENIZED LOADING
 * ───────────────────────────────────────────────────────────── */

export function HeaderSkeleton() {
  return (
    <div className="bg-white border-b border-zinc-200/80 px-6 py-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-zinc-200 rounded-xl" />
          <div className="space-y-2">
            <div className="h-5 w-40 bg-zinc-200 rounded" />
            <div className="h-3 w-64 bg-zinc-100 rounded" />
          </div>
        </div>
        <div className="flex gap-2">
          <div className="h-9 w-24 bg-zinc-200 rounded-lg" />
          <div className="h-9 w-32 bg-zinc-300 rounded-lg" />
        </div>
      </div>
    </div>
  );
}

export function TableSkeleton({ rows = 6 }: { rows?: number }) {
  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      <HeaderSkeleton />
      <div className="p-6 space-y-4 flex-1">
        {/* Search & Filter Bar Skeleton */}
        <div className="bg-white p-4 rounded-xl border border-zinc-200 flex items-center justify-between gap-4 animate-pulse">
          <div className="h-10 w-72 bg-zinc-100 rounded-xl" />
          <div className="flex gap-2">
            <div className="h-10 w-28 bg-zinc-100 rounded-xl" />
            <div className="h-10 w-28 bg-zinc-100 rounded-xl" />
          </div>
        </div>

        {/* Table Skeleton Box */}
        <div className="bg-white rounded-xl border border-zinc-200 overflow-hidden shadow-xs animate-pulse">
          <div className="h-12 bg-zinc-100/80 border-b border-zinc-200 px-6 flex items-center gap-4">
            <div className="h-4 w-12 bg-zinc-200 rounded" />
            <div className="h-4 w-48 bg-zinc-200 rounded" />
            <div className="h-4 w-24 bg-zinc-200 rounded" />
            <div className="h-4 w-32 bg-zinc-200 rounded" />
          </div>
          <div className="divide-y divide-zinc-100">
            {Array.from({ length: rows }).map((_, i) => (
              <div
                key={`table-skel-${i + 1}`}
                className="px-6 py-4 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-3 flex-1">
                  <div className="h-4 w-10 bg-zinc-200 rounded" />
                  <div className="h-4 w-64 bg-zinc-200 rounded" />
                </div>
                <div className="h-6 w-20 bg-zinc-100 rounded-full" />
                <div className="h-6 w-24 bg-zinc-100 rounded-full" />
                <div className="h-4 w-28 bg-zinc-100 rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function KanbanSkeleton() {
  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      <HeaderSkeleton />
      <div className="p-6 flex-1 flex gap-4 overflow-x-auto">
        {Array.from({ length: 4 }).map((_, colIdx) => (
          <div
            key={`kanban-col-skel-${colIdx + 1}`}
            className="w-80 bg-zinc-100/70 border border-zinc-200/80 rounded-2xl p-4 flex flex-col gap-3 flex-shrink-0 animate-pulse"
          >
            <div className="flex items-center justify-between mb-1">
              <div className="h-5 w-28 bg-zinc-200 rounded" />
              <div className="h-5 w-6 bg-zinc-200 rounded-full" />
            </div>
            {Array.from({ length: 3 }).map((_, cardIdx) => (
              <div
                key={`kanban-card-skel-${colIdx + 1}-${cardIdx + 1}`}
                className="bg-white p-4 rounded-xl border border-zinc-200 space-y-3 shadow-xs"
              >
                <div className="h-4 w-3/4 bg-zinc-200 rounded" />
                <div className="h-3 w-full bg-zinc-100 rounded" />
                <div className="flex items-center justify-between pt-2">
                  <div className="h-5 w-16 bg-zinc-100 rounded-full" />
                  <div className="h-6 w-6 bg-zinc-200 rounded-full" />
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function GridSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      <HeaderSkeleton />
      <div className="p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 overflow-y-auto">
        {Array.from({ length: count }).map((_, i) => (
          <div
            key={`grid-skel-${i + 1}`}
            className="bg-white p-6 rounded-2xl border border-zinc-200/80 space-y-4 shadow-xs animate-pulse"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-zinc-200 rounded-xl" />
              <div className="space-y-2 flex-1">
                <div className="h-4 w-32 bg-zinc-200 rounded" />
                <div className="h-3 w-20 bg-zinc-100 rounded" />
              </div>
            </div>
            <div className="h-12 w-full bg-zinc-100 rounded-lg" />
            <div className="flex items-center justify-between pt-2 border-t border-zinc-100">
              <div className="h-4 w-16 bg-zinc-100 rounded" />
              <div className="h-7 w-20 bg-zinc-200 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function CalendarSkeleton() {
  return (
    <div className="h-full flex flex-col bg-zinc-50 overflow-hidden">
      <HeaderSkeleton />
      <div className="p-6 flex-1 flex gap-6 overflow-hidden">
        <div className="flex-1 bg-white rounded-2xl border border-zinc-200/80 p-6 flex flex-col animate-pulse">
          <div className="grid grid-cols-7 gap-2 mb-3">
            {Array.from({ length: 7 }).map((_, i) => (
              <div key={`cal-day-skel-${i + 1}`} className="h-4 bg-zinc-100 rounded text-center" />
            ))}
          </div>
          <div className="grid grid-cols-7 gap-2 flex-1 auto-rows-fr">
            {Array.from({ length: 35 }).map((_, i) => (
              <div
                key={`cal-cell-skel-${i + 1}`}
                className="bg-zinc-50 border border-zinc-100 rounded-xl p-2"
              />
            ))}
          </div>
        </div>
        <div className="w-80 bg-white rounded-2xl border border-zinc-200 p-4 space-y-4 animate-pulse hidden lg:block">
          <div className="h-5 w-36 bg-zinc-200 rounded" />
          <div className="h-20 bg-zinc-100 rounded-xl" />
          <div className="h-20 bg-zinc-100 rounded-xl" />
        </div>
      </div>
    </div>
  );
}
